import { useEffect, useState } from "react";

import { applyTheme, readTheme, storeTheme, type Theme } from "../../lib/theme";

/**
 * Cycles Auto → Light → Dark.
 *
 * "Auto" is the default and follows the operating system. Applying it is *not* this
 * component's job any more: it only renders on signed-in pages, so while it owned the
 * theme the sign-in and email-confirmation screens were light whatever anyone had picked,
 * and every load painted light before React arrived. `public/theme.js` does it before the
 * first paint; this writes the choice and re-applies it.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(readTheme);

  // Only ever a *change*: the pre-paint script already applied whatever is stored, and
  // re-running it on mount would be a second write of the same value.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const next: Record<Theme, Theme> = { system: "light", light: "dark", dark: "system" };
  const label: Record<Theme, string> = { system: "Auto", light: "Light", dark: "Dark" };

  return (
    <button
      type="button"
      onClick={() => {
        const chosen = next[theme];
        setTheme(chosen);
        storeTheme(chosen);
      }}
      className="tap rounded px-2 text-xs text-muted hover:text-ink"
      aria-label={`Theme: ${label[theme]}. Switch to ${label[next[theme]]}.`}
    >
      {label[theme]}
    </button>
  );
}
